import { AgentChatMessage } from "@coeff/api";

/**
 * Mirror of thread_analysis._strip_coeff_context: every user turn has the client's coeff_context
 * JSON appended, and the analyzer drops it before rendering.
 */
function stripCoeffContext(text: string): string {
  const marker = text.indexOf("coeff_context:");
  return (marker === -1 ? text : text.slice(0, marker)).trim();
}

function normalize(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Mirror of thread_analysis.MAX_MESSAGES_FETCHED. The analyzer only ever sees the newest N
 * messages, and numbers turns from the oldest one it retained — so this view has to start
 * counting from the same place. The admin messages endpoint returns the whole thread, which for
 * a thread longer than this would otherwise put U1 on the wrong message and shift every anchor.
 */
const MAX_MESSAGES_FETCHED = 1000;

interface UserTurn {
  id: string;
  text: string;
}

/**
 * User turns by number, matching `thread_analysis.build_transcript` / `_number_user_turns`: a
 * turn is a user message whose text is non-empty once coeff_context is stripped, numbered from 1
 * in chronological order over the analyzer's message window. A user message that is only
 * coeff_context never renders in the transcript and so never consumes a number — counting it here
 * would shift every later anchor.
 */
export function userTurnsByNumber(messages: AgentChatMessage[]): Map<number, UserTurn> {
  const byTurn = new Map<number, UserTurn>();
  let turn = 0;
  messages.slice(-MAX_MESSAGES_FETCHED).forEach(msg => {
    if (msg.role !== "user") {
      return;
    }
    const text = stripCoeffContext(msg.content?.text ?? "");
    if (!text) {
      return;
    }
    turn += 1;
    if (msg.id) {
      byTurn.set(turn, { id: msg.id, text: normalize(text) });
    }
  });
  return byTurn;
}

/**
 * Resolves a finding's anchor to a message id: `first_user_turn` first, falling back to matching
 * `user_quote` against the turns.
 *
 * The turn is the primary because it is the more reliable of the two — measured over the first
 * production run it landed 34/42, and over the hand-audited threads it was right every time,
 * while quotes resolve uniquely only ~74% (users re-send identical text, and the model sometimes
 * paraphrases). But the failures are not evenly spread: one thread came back with
 * first_user_turn 0 on every finding, and the quotes there pointed at the right messages. So the
 * quote earns its place as a fallback, never as a veto — a quote that fails to match must not
 * suppress a jump the turn already resolved.
 *
 * Returns undefined when neither lands, and the caller renders no jump rather than a wrong one.
 */
export function resolveAnchor(
  turns: Map<number, UserTurn>,
  anchor: { turn?: number; quote?: string }
): string | undefined {
  if (anchor.turn !== undefined) {
    const hit = turns.get(anchor.turn);
    if (hit) {
      return hit.id;
    }
  }
  const quote = normalize(anchor.quote ?? "");
  if (!quote) {
    return undefined;
  }
  // Earliest match: a struggle is anchored to where the user FIRST raised it, and re-sent text is
  // exactly the case where a quote matches several turns.
  const numbers = Array.from(turns.keys()).sort((a, b) => a - b);
  for (const n of numbers) {
    const candidate = turns.get(n);
    if (candidate && candidate.text.includes(quote)) {
      return candidate.id;
    }
  }
  return undefined;
}
